import { CrudFilters } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { filter, sorter } from 'utility'
import { currentUserEmail } from './authLocal'
import { removeNull } from './cleanObjectTypescript'
import { ADMIN_ROLE_ID, ADMITTED_STATUS, API_URL, BASIC_USER_ROLE_ID, CUSTOM_ORDER_MARKETPLACE_ID, CUSTOM_ORDER_TEXT, DASHBOARD_USER_MIN_ROLE_ID, DECREASE_RETURN_STATUS, DEFAULT_IVA, DELIVERED_STATUS, ECONOMY_SHIPMENT_METHOD_ID, IGIC_TYPE_REDUCIDO, INTERNAL_SPENDING_RETURN_STATUS, LOW_VALUE_LIMIT, MAX_RETURN_DEADLINE, MAX_ROWS_QUERY, MAX_ROWS_REFUNDS, MAX_ROWS_SALES, NO_STOCK_STATUS, ORDER_STATUS_ID_PENDING_PAYMENT, ORDER_STATUS_ID_REQUESTED, PARCIAL_RETURN_STATUS, PENALIZATION_STATUS, PREPARED_STATUS, PROCESSED_STATUS, PURCHASE_USER_ROLE_ID, REFUND_STATUS, RETURN_STATUS, SHIPPED_STATUS, SHIPPING_INSURANCE, SHIPPING_METHOD_REDUCED_ID, SPECIAL_MARKETPLACES, SUPPLEMENT_STATUS, TIPSA_AGENCY_CODE, TIPSA_AGENCY_SERVICE_TYPE_CONSOLIDATED, TIPSA_AGENCY_SERVICE_TYPE_DIRECT, VAR_BENEFIT_MIN_ORDER } from './constants'
import { formatDateTime } from './formatDateTime'
import { savings, shippingInsurance, subtotal, synivaPrice, total } from './orderCalculator'
import { supabaseClient } from './supabaseClient'
import { MapStatusIDToText, PostgrestFilterBuilder, getIslands, getReturnDayLimit, postalToIsland } from './utils'

// Helper function to convert Date to ISO format AAAA-MM-DD
const dateToISOFormat = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

export const getOrders = async (select: string, page: number, itemsPerRow: number, filters?: filter[], sorter?: sorter) => {
  let stringFilter = ''
  let stringSorter = ''
  if (filters) {
    filters.forEach((filt: filter) => {
      if (filt.operator === 'not') {
        stringFilter += `.${filt.operator}('${filt.field}', 'is', null)`
      } else if (filt.operator === 'is') {
        stringFilter += `.${filt.operator}('${filt.field}', ${filt.value})`
      } else if (filt.operator === 'in') {
        stringFilter += `.${filt.operator}('${filt.field}', [${filt.value}])`
      } else if (filt.operator === 'notin') {
        stringFilter += `.not('${filt.field}', 'in', '(${filt.value})')`
      } else if (filt.operator === 'or') {
        stringFilter += `.${filt.operator}('${filt.value}')`
      } else if (filt.operator === 'filter') {
        if (filt.secondOp === 'ilike') {
          stringFilter += `.${filt.operator}('${filt.field}', '${filt.secondOp}','%${filt.value}%')`
        } else {
          stringFilter += `.${filt.operator}('${filt.field}', '${filt.secondOp}',${(typeof filt.value === 'string') ? "'" + filt.value + "'" : (Array.isArray(filt.value)) ? '[' + filt.value + ']' : filt.value})`
        }
      } else {
        stringFilter += `.${filt.operator}('${filt.field}',${(typeof filt.value === 'string') ? "'" + filt.value + "'" : (Array.isArray(filt.value)) ? '[' + filt.value + ']' : filt.value})`
      }
    })
  }

  if (sorter) {
    if (sorter.field === 'executable_sort_at') {
      stringSorter = `.order('executable_sort_at', { ascending: ${sorter.ascending}, nullsFirst: false }).order('id', { ascending: false })`
    } else if (sorter.field === 'id') {
      stringSorter = '.order(\'negative_id\', { ascending: true })'
    } else {
      stringSorter = `.order('${sorter.field}', { ascending: ${sorter.ascending} })`
    }
  }

  const stringRange = (page === -1 && itemsPerRow === 0) ? '' : `.range((${page} - 1) * ${itemsPerRow}, ((${page} - 1) * ${itemsPerRow}) + ${itemsPerRow} - 1)`

  const funcBodyCount = `async function func(supabaseClient) { const { count } = await supabaseClient.from('orders').select('${select}', { count: 'exact', head: true })${stringFilter}; return count; }`
  const wrap = (s: any) => `{ return ${funcBodyCount}; }`
  // eslint-disable-next-line no-new-func
  const funcCount = new Function(wrap(funcBodyCount))
  // eslint-disable-next-line no-useless-call
  const count = await funcCount.call(null).call(null, supabaseClient)

  const funcBodyData = `async function func(supabaseClient) { const { data, error } = await supabaseClient.from('orders').select('${select}')${stringFilter}${stringRange}${stringSorter}; return { data, error }; }`
  const wrapData = (s: any) => `{ return ${funcBodyData}; }`
  // eslint-disable-next-line no-new-func
  const funcData = new Function(wrapData(funcBodyData))
  // eslint-disable-next-line no-useless-call
  const { data, error } = await funcData.call(null).call(null, supabaseClient)

  if (error) return { data: [], count: 0 }
  return { data: data, count: count }
}

export const updateCalculatedTotalOrder = async (id: number) => {
  const { data, error } = await supabaseClient.from('orders')
    .select('id, total, discount, wallet_amount_used, subscription_cost, supplements(id, deleted, volumetric, description, supplement)')
    .eq('id', id).single()

  if (error) return null

  let orderFinalTotal = data.total || 0

  if (data && data.supplements && data.supplements.length > 0) {
    const volumetricSupplements = data.supplements.filter((s: any) => s.volumetric && !s.deleted)
      .map((s: any) => s.supplement || 0)
      .reduce((a: number, b: number) => a + b, 0)

    const nonVolumetricSupplements = data.supplements.filter((s: any) => !s.volumetric && !s.deleted &&
      s.description !== 'reducedIva' &&
      s.description !== 'reducedIvaSecondHand' &&
      s.description !== 'reducedIvaBreakdown' &&
      s.description !== 'reducedIvaEU' &&
      s.description !== 'reducedIvaShipment' &&
      s.description !== 'refund' &&
      s.description !== 'igic' &&
      s.description !== 'shippingInsurancePrice' &&
      s.description !== 'refundStore' &&
      s.description !== 'refundByCancelation' &&
      s.description !== 'refundTransport' &&
      s.description !== 'transportToPeninsula' &&
      s.description !== 'transportSupplement' &&
      s.description !== 'unified' &&
      s.description !== 'priceChange')
      .map((s: any) => s.supplement || 0)
      .reduce((a: number, b: number) => a + b, 0)

    const refundTransportSupplements = data.supplements.filter((s: any) => !s.volumetric && !s.deleted &&
      (s.description === 'refundTransport' || s.description === 'transportToPeninsula' || s.description === 'transportSupplement'))
      .map((s: any) => s.supplement || 0)
      .reduce((a: number, b: number) => a + b, 0)

    const refundSupplements = data.supplements.filter((s: any) => !s.volumetric && !s.deleted &&
      (s.description === 'refund' || s.description === 'refundStore'))
      .map((s: any) => s.supplement || 0)
      .reduce((a: number, b: number) => a + b, 0)

    // refundByCancelation is a special case, we don't need it in the summary

    orderFinalTotal += volumetricSupplements + nonVolumetricSupplements + refundTransportSupplements + refundSupplements + (data.discount || 0) - (data.wallet_amount_used || 0)
  } else {
    orderFinalTotal += (data.discount || 0) - (data.wallet_amount_used || 0)
  }

  const { error: errorUpdate } = await supabaseClient.from('orders')
    .update({
      current_calculated_total: parseFloat(orderFinalTotal).toFixed(2),
      updated_by: supabaseClient.auth.user()?.email
    })
    .eq('id', id)

  if (errorUpdate) return null
  else return id
}

export const getOrderExport = async (select: string, filters?: filter[], sorter?: sorter) => {
  const { data } = await getOrders(select, -1, 0, filters, sorter)
  return data
}

export const getItemsOfProcessedOrdersExport = async () => {
  const { data, error } = await supabaseClient.rpc('items_processed')
  if (error) {
    console.error(error)
    return { data: [], error }
  }
  return { data, error: null }
}

export const findUsersByText = async (value: string) => {
  const { data } = await supabaseClient.from('users')
    .select('id, email')
    .ilike('email', `%${value}%`)
    .eq('role_id', BASIC_USER_ROLE_ID)
    .order('id', { ascending: true })

  return data
}

export const findAdminUsersByText = async (value: string) => {
  const { data } = await supabaseClient.from('users')
    .select('id, email')
    .ilike('email', `%${value}%`)
    .in('role_id', [ADMIN_ROLE_ID, DASHBOARD_USER_MIN_ROLE_ID, PURCHASE_USER_ROLE_ID])
    .order('id', { ascending: true })

  return data
}

export const findMarketplacesByText = async (value: string) => {
  const { data } = await supabaseClient.from('marketplaces')
    .select('id, name')
    .ilike('name', `%${value}%`)
    .eq('deleted', false)
    .order('id', { ascending: true })

  return data
}

export const findOrdersByIdWithoutIssues = async (value: string) => {
  const { data } = await supabaseClient
    .from('orders')
    .select('*, issues(order_id), marketplace_id(name), status(id, name), shipping_method(name)')
    .like('id_to_text', `%${value}%`)
    .order('id', { ascending: true })

  return data
}

// 1, get all orders that have invoice date between gtDate and ltDate, and that have invoice not null
const getOrderIdsWithInvoiceByDates = async (
  gtDate: Date,
  ltDate: Date,
  reviewed: boolean
): Promise<number[]> => {
  const gtDateFormat = new Date(gtDate)
  const ltDateFormat = new Date(ltDate)
  const isoDateGt = dateToISOFormat(gtDateFormat)
  const isoDateLt = dateToISOFormat(ltDateFormat)

  const query = supabaseClient
    .from('invoices')
    .select('order_id')
    .eq('reviewed', reviewed)
    .gte('invoice_date', isoDateGt)
    .lte('invoice_date', isoDateLt)

  const result = await query

  if (result.status !== 500) {
    return result.data?.map((o: any) => o.order_id) || []
  }

  const diff = ltDateFormat.getTime() - gtDateFormat.getTime()
  const MIN_INTERVAL_MS = 60 * 60 * 1000

  if (diff <= MIN_INTERVAL_MS) {
    console.error('Min interval reached for orders query, returning error: ', gtDateFormat, ltDateFormat)
    return []
  }

  const middle = new Date(gtDateFormat.getTime() + diff / 2)

  const left = await getOrderIdsWithInvoiceByDates(gtDateFormat, middle, reviewed)
  const right = await getOrderIdsWithInvoiceByDates(middle, ltDateFormat, reviewed)

  return [...left, ...right]
}

// Query 2: get items using previoues order ids
const getItemsByOrderIds = async (
  select: string,
  orderIds: number[]
): Promise<any[]> => {
  if (orderIds.length === 0) {
    return []
  }

  // If you sent a lot of ids in a single query, it could cause a 500 error, so we divide the orderIds
  const CHUNK_SIZE = 100
  const chunks: number[][] = []
  for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
    chunks.push(orderIds.slice(i, i + CHUNK_SIZE))
  }

  // Process by chunks
  const allItems: any[] = []
  const itemPromises = chunks.map(async (chunk) => {
    const baseQuery = supabaseClient
      .from('items')
      .select(`${select}`)
      .eq('deleted', false)
      .in('order_id', chunk)
    const result = await baseQuery

    if (result.status === 500) {
      // if 500, divide the chunk into two and try again recursively
      const mid = Math.ceil(chunk.length / 2)
      const left = await getItemsByOrderIds(select, chunk.slice(0, mid))
      const right = await getItemsByOrderIds(select, chunk.slice(mid))
      return [...left, ...right]
    }

    return result.data || []
  })

  const itemsPerChunk = await Promise.all(itemPromises)
  itemsPerChunk.forEach((items) => {
    allItems.push(...items)
  })

  // if reviewed is false, filter by supplements too
  // if (!reviewed && allItems.length > 0) {
  //   const supplementPromises = chunks.map(async (chunk) => {
  //     const { data: validSupplements } = await supabaseClient
  //       .from('supplements')
  //       .select('order_id')
  //       .in('order_id', chunk)

  //     return validSupplements || []
  //   })

  //   const allSupplements = await Promise.all(supplementPromises)
  //   const validOrderIds = new Set<number>()

  //   allSupplements.forEach((supplements) => {
  //     supplements.forEach((s: any) => {
  //       if (s.order_id) {
  //         validOrderIds.add(s.order_id)
  //       }
  //     })
  //   })

  //   return allItems.filter((item: any) => validOrderIds.has(item.order_id))
  // }

  return allItems
}

const runItemsQueryByDates = async (
  select: string,
  reviewed: boolean,
  gtDate: Date,
  ltDate: Date
): Promise<any[]> => {
  const orderIds = await getOrderIdsWithInvoiceByDates(gtDate, ltDate, reviewed)
  const items = await getItemsByOrderIds(select, orderIds)
  const activeOrderIds = new Set(items.filter(item => !item.disabled).map(item => item.order_id))
  const missingOrderIds = Array.from(new Set(orderIds)).filter(id => !activeOrderIds.has(id))
  // Keep invoiced services visible without creating a persisted dummy item.
  const orderStart = select.indexOf('order(')
  if (orderStart < 0 || !select.endsWith(')')) throw new Error('Missing sales order projection')
  const orderSelect = select.slice(orderStart + 6, -1)
  for (let offset = 0; offset < missingOrderIds.length; offset += 100) {
    const { data, error } = await supabaseClient.from('orders').select(orderSelect)
      .in('id', missingOrderIds.slice(offset, offset + 100))
    if (error) throw error
    for (const order of data || []) {
      items.push({
        id: null,
        order_id: order.id,
        name: 'Servicios facturados',
        quantity: 0,
        syniva_price: 0,
        igic_price: 0,
        igic_percent: 0,
        isInvoiceOnly: true,
        order
      })
    }
  }
  return items
}

export const getSales = async (select: string, reviewed: boolean = false, gtDate: Date, ltDate: Date) => {
  const querySucess = await runItemsQueryByDates(
    select,
    reviewed,
    gtDate,
    ltDate
  )

  return querySucess
}

export const getRefunds = async (select: string, reviewed: boolean = false, gtDate: Date, ltDate: Date, isCorrection: boolean = false) => {
  let counter = 0
  const { count: countRefund } = await supabaseClient.from('refund_invoice_data')
    .select(`${select}`, { count: 'exact', head: true })
    .eq('reviewed', reviewed)
    .not('refund_number', 'is', null)
    .gte('refund_date', gtDate)
    .lte('refund_date', ltDate)
    .is('original_order_invoice', null)

  const { count: countCorrection } = await supabaseClient.from('refund_invoice_data')
    .select(`${select}`, { count: 'exact', head: true })
    .eq('reviewed', reviewed)
    .not('refund_number', 'is', null)
    .gte('refund_date', gtDate)
    .lte('refund_date', ltDate)
    .not('original_order_invoice', 'is', null)

  counter = isCorrection ? countCorrection || 0 : countRefund || 0

  let maxIterations = Math.floor((counter || 0) / MAX_ROWS_REFUNDS)

  if ((counter || 0) % MAX_ROWS_REFUNDS > 0) {
    maxIterations += 1
  }

  const iterateArray = new Array(maxIterations)
  const promiseArray = []
  const range = [0, MAX_ROWS_REFUNDS - 1]
  // eslint-disable-next-line no-unused-vars
  for (const index of iterateArray) {
    if (isCorrection) {
      promiseArray.push(supabaseClient.from('refund_invoice_data')
        .select(`${select}`)
        .eq('reviewed', reviewed)
        .not('refund_number', 'is', null)
        .gte('refund_date', gtDate)
        .lte('refund_date', ltDate)
        .not('original_order_invoice', 'is', null)
        .range(range[0], range[1])
      )
    } else {
      promiseArray.push(supabaseClient.from('refund_invoice_data')
        .select(`${select}`)
        .eq('reviewed', reviewed)
        .not('refund_number', 'is', null)
        .gte('refund_date', gtDate)
        .lte('refund_date', ltDate)
        .range(range[0], range[1])
        .is('original_order_invoice', null)
      )
    }
    range[0] += MAX_ROWS_REFUNDS
    range[1] += MAX_ROWS_REFUNDS
  }

  const results = await Promise.all(promiseArray)
  let querySucess: any[] = []
  results.forEach((r) => {
    if (r.data) {
      querySucess = [...querySucess, ...r.data]
    }
  })

  return querySucess
}

export const getPurchases = async (select: string, reviewed: boolean, gtDate: Date, ltDate: Date) => {
  let counter = 0
  if (reviewed) {
    const { count } = await supabaseClient.from('market_orders')
      .select(`${select}`, { count: 'exact', head: true })
      .eq('deleted', false)
      .eq('reviewed', true)
      .gte('invoice_date', gtDate)
      .lte('invoice_date', ltDate)
    counter = count || 0
  } else {
    const { count } = await supabaseClient.from('market_orders')
      .select(`${select}`, { count: 'exact', head: true })
      .eq('deleted', false)
      .eq('reviewed', false)
      .gte('invoice_date', gtDate)
      .lte('invoice_date', ltDate)
    counter = count || 0
  }

  let maxIterations = Math.floor((counter || 0) / MAX_ROWS_SALES)

  if ((counter || 0) % MAX_ROWS_SALES > 0) {
    maxIterations += 1
  }

  const iterateArray = new Array(maxIterations)
  const promiseArray = []

  const range = [0, MAX_ROWS_SALES - 1]
  // eslint-disable-next-line no-unused-vars
  for (const index of iterateArray) {
    if (reviewed) {
      promiseArray.push(supabaseClient.from('market_orders')
        .select(`${select}`)
        .eq('deleted', false)
        .eq('reviewed', true)
        .gte('invoice_date', gtDate)
        .lte('invoice_date', ltDate)
        .range(range[0], range[1])
      )
    } else {
      promiseArray.push(supabaseClient.from('market_orders')
        .select(`${select}`)
        .eq('deleted', false)
        .eq('reviewed', false)
        .gte('invoice_date', gtDate)
        .lte('invoice_date', ltDate)
        .range(range[0], range[1])
      )
    }
    range[0] += MAX_ROWS_SALES
    range[1] += MAX_ROWS_SALES
  }

  const results = await Promise.all(promiseArray)

  let querySucess: any[] = []
  results.forEach((r) => {
    if (r.data) {
      querySucess = [...querySucess, ...r.data]
    }
  })
  return querySucess
}

export const getSupplierExport = async (select: string, filter: filter[] | null, seller?: boolean) => {
  let dataFind = []

  if (!filter) {
    const { data } = await supabaseClient.from('suppliers')
      .select(select)
      .eq('deleted', false)
      .eq('seller', !!seller)
    dataFind = data || []
  } else {
    const { data } = await supabaseClient.from('suppliers')
      .select(select)
      .eq('deleted', false)
      .eq('seller', !!seller)
      .ilike(filter[0].field, `%${filter[0].value}%`)
    dataFind = data || []
  }

  return dataFind
}

export const getVariableValue = async (variableName: string, setState: Function) => {
  const { data } = await supabaseClient
    .from('variables')
    .select('value')
    .eq('name', variableName)
    .single()

  setState(Number(data.value))
}

export const getVariable = async (variableName: string) => {
  const { data } = await supabaseClient
    .from('variables')
    .select('value')
    .eq('name', variableName)
    .single()

  return data.value || null
}

export const getCountRequestedOrders = async () => {
  const { count } = await supabaseClient
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('status_id', ORDER_STATUS_ID_REQUESTED)
  return count
}

export const getCountPriorityOrders = async () => {
  const { count } = await supabaseClient
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('is_priority', true)
    .in('status_id', [ORDER_STATUS_ID_REQUESTED, PROCESSED_STATUS, SUPPLEMENT_STATUS, ADMITTED_STATUS])
  return count
}

export const lockOrUnlockOrder = async (orderId: number | undefined, value: boolean) => {
  if (orderId) {
    const lockedBy = value ? currentUserEmail() || supabaseClient.auth.user()?.email : null
    await supabaseClient
      .from('orders')
      .update({
        locked: value,
        locked_by: lockedBy,
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('id', orderId)
  }
}

export const handleDisableItem = async (itemId: number[], value: boolean, shouldReload: boolean = true, orderStatus?: number | undefined) => {
  if (itemId) {
    const { data: tempItem } = await supabaseClient
      .from('items')
      .update({ disabled: value, updated_by: supabaseClient.auth.user()?.email })
      .in('id', itemId)

    const orderId = tempItem ? tempItem[0].order_id : null

    const { data: order } = await supabaseClient
      .from('orders')
      .select('items(id, disabled), shipping_method(shipMode)')
      .eq('id', orderId)
      .single()

    const shipMode = order?.shipping_method?.shipMode || false

    // Check if the invoice is editable before making changes
    const { editable } = await checkIfInvoiceIsEditable(orderId)

    await calculateOrderPrices(orderId, true, editable)

    // Check if need puchase_conditions applications
    if (shipMode && (orderStatus && orderStatus !== PENALIZATION_STATUS)) {
      const { data: initialOrder } = await supabaseClient.from('audit_orders')
        .select('id, record, resource_id')
        .eq('resource_id', orderId)
        .order('created_at', { ascending: true })
        .limit(1)

      const initialSynivaPrice = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.syniva_price || 0 : 0

      const { data: currentOrder } = await supabaseClient.from('orders').select('id, syniva_price, shipping_price, purchase_conditions').eq('id', orderId).single()
      const purchaseConditions = currentOrder?.purchase_conditions || null
      const shippingPriceToAdd = parseFloat(purchaseConditions.shipment_method_price)

      if (purchaseConditions && purchaseConditions.free_vol && purchaseConditions.free_vol === 'true') {
        const { data: transportSupsData } = await supabaseClient.from('supplements')
          .select('id, supplement, description')
          .eq('order_id', orderId)
          .eq('deleted', false)
          .or(`supplement.eq.${shippingPriceToAdd}, supplement.eq.${-shippingPriceToAdd}`)
          .or('description.eq.refundTransport, description.eq.transportSupplement')

        const totalSupsData = transportSupsData?.map((x: any) => x.supplement).reduce((a: number, b: number) => a + b, 0) || 0

        if (purchaseConditions.free_vol_price_min) {
          if (parseFloat(purchaseConditions.free_vol_price_min) < currentOrder.syniva_price && initialSynivaPrice < parseFloat(purchaseConditions.free_vol_price_min)) {
            // console.log(1, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
            if (totalSupsData === 0) {
              // console.log('A')
              await addTransportSupplementOrRefund(orderId, 'refundTransport', ADMITTED_STATUS, -purchaseConditions.shipment_method_price)
            } else {
              // console.log('B')
            }
          } else if (parseFloat(purchaseConditions.free_vol_price_min) < initialSynivaPrice && currentOrder.syniva_price > parseFloat(purchaseConditions.free_vol_price_min)) {
            // console.log(3, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
            if (totalSupsData === 0) {
              // console.log('A')
            } else {
              // console.log('B')
              await addTransportSupplementOrRefund(orderId, 'refundTransport', ADMITTED_STATUS, -purchaseConditions.shipment_method_price)
            }
          } else if (parseFloat(purchaseConditions.free_vol_price_min) < initialSynivaPrice && currentOrder.syniva_price < parseFloat(purchaseConditions.free_vol_price_min)) {
            // console.log(2, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
            if (totalSupsData === 0) {
              // console.log('A')
              await addTransportSupplementOrRefund(orderId, 'transportSupplement', (purchaseConditions.shipment_method_price > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS, purchaseConditions.shipment_method_price)
            } else {
              // console.log('B')
            }
          } else if (parseFloat(purchaseConditions.free_vol_price_min) > currentOrder.syniva_price && initialSynivaPrice < parseFloat(purchaseConditions.free_vol_price_min)) {
            // console.log(4, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
            if (totalSupsData === 0) {
              // console.log('A')
            } else {
              await addTransportSupplementOrRefund(orderId, 'transportSupplement', (purchaseConditions.shipment_method_price > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS, purchaseConditions.shipment_method_price)
              // console.log('B')
            }
          }
        }
      }
    }

    if (shouldReload) window.location.reload()
  }
}

export const calculateOrderPrices = async (orderId: number, comeFromShipmentChange: boolean = true, editable: boolean) => {
  const [itemsResponse, orderResponse, igicTypesResponse] = await Promise.all([
    supabaseClient.from<definitions['items']>('items')
      .select('quantity, syniva_price, price, tariff_price, igic_price, igic_percent, iva, disabled, has_accessories, item_category_id(id, igic_percent), shipping_price_to_madrid, url, custom_order_site')
      .eq('order_id', orderId)
      .eq('deleted', false),
    supabaseClient.from('orders')
      .select('id, created_at, status_id, subtotal, syniva_price, subscription_cost, total, type, igic, shipping_price, shipping_insurance_price, shipping_method_id, marketplace_id, shipping_method(price, shipMode), discount, wallet_amount_used, custom_order_price, promotional_code, purchase_conditions')
      .eq('id', orderId)
      .single(),
    supabaseClient.from('igic_types')
      .select('id, name, value')
  ])

  const items = itemsResponse.data
  const order = orderResponse.data
  const igicTypes = igicTypesResponse.data

  // console.log(items, order, igicTypes)

  const { data: marketplace } = await supabaseClient
    .from('marketplaces')
    .select('igic_types(value), id, shipment_price, updated_at, free_shipment_price')
    .eq('id', order.marketplace_id)
    .single()

  const { data: marketplaceAudit } = await supabaseClient
    .from('audit_marketplaces')
    .select('id, record->>shipment_price, record->>created_at, created_at, resource_id')
    .eq('resource_id', marketplace?.id)
    .order('created_at', { ascending: false })
    .limit(100)

  const activeItems = items?.filter((row) => !row.disabled) || null

  const shipMode = order.shipping_method?.shipMode || false

  const { data: insurancePercentage } = await supabaseClient.from('variables').select('value').eq('name', SHIPPING_INSURANCE).single()
  const { data: lowValueLimit } = await supabaseClient.from('variables').select('value').eq('name', LOW_VALUE_LIMIT).single()

  const subtotalValue = subtotal(activeItems)
  let savingsValue = savings(activeItems, DEFAULT_IVA)
  let synivaPriceValue = synivaPrice(activeItems)
  // FIXME: Fixed using date, best try to set the order structure with a migration
  let shippingInsuranceValue = shippingInsurance(activeItems, insurancePercentage.value, shipMode)
  shippingInsuranceValue = (order.shipping_method_id === SHIPPING_METHOD_REDUCED_ID) ? 0 : shippingInsuranceValue
  const deployDate = new Date('2024-03-06T15:00:00.00000+00:00')
  const createdDate = new Date(order.created_at)
  if (createdDate < deployDate) {
    shippingInsuranceValue = shippingInsurance(activeItems, 2, shipMode)
  }
  let shippingPriceToMadrid = 0

  activeItems?.forEach((i) => {
    if (i.shipping_price_to_madrid && i.shipping_price_to_madrid !== null && shippingPriceToMadrid < i.shipping_price_to_madrid) {
      shippingPriceToMadrid = i.shipping_price_to_madrid
    }
  })

  if (shipMode) {
    savingsValue = 0
    synivaPriceValue = subtotalValue
  }

  const isCustom = order.type.toLowerCase() === CUSTOM_ORDER_TEXT
  let igicPercentage = marketplace.igic_types.value
  if (isCustom) {
    const withAccesories = items?.filter(x => x.has_accessories === true)
    if (withAccesories && withAccesories.length > 0) {
      // eslint-disable-next-line no-unused-vars
      igicPercentage = igicTypes?.find(x => x.name.toLowerCase() === IGIC_TYPE_REDUCIDO)?.value || 3
    }
  }
  // const withCategory = items?.filter(x => x.item_category_id !== null)

  // if (withCategory && withCategory.length > 0) {
  //   const igicData = withCategory.map(y => (y.item_category_id.igic_percent as number) || 0)
  //   igicPercentage = Math.max(...igicData)
  // }

  // console.log(igicPercentage)

  let igicValue = 0
  // its for all active items? or only if SynivaPrice > 150 in order
  activeItems?.forEach((i: any) => {
    if (synivaPriceValue > lowValueLimit.value) {
      igicValue += i.igic_price
    }
  })

  let isPromotedFreeShipment = false

  if (marketplaceAudit !== null && marketplaceAudit.length > 0 && order?.created_at) {
    const maWithFreeShipment = marketplaceAudit.filter(x => x.shipment_price < 0)
    maWithFreeShipment.forEach((ma) => {
      ma.record = { shipment_price: ma.shipment_price || '0', created_at: ma.created_at }
      const index = marketplaceAudit.findIndex((x) => x.id === ma.id)
      const indexEnd = marketplaceAudit.findIndex((x) => x.created_at > marketplaceAudit[index].created_at && x?.shipment_price > 0)
      const init = marketplaceAudit[index]
      const end = marketplaceAudit[indexEnd] || undefined

      if (init && end !== undefined) {
        const orderDate = new Date(order?.created_at)
        const marketDateStart = new Date(init.created_at)
        const marketDateEnd = new Date(end.created_at)
        const isIn = orderDate.getTime() < marketDateEnd.getTime() && orderDate.getTime() > marketDateStart.getTime()
        if (isIn) {
          isPromotedFreeShipment = true
        }
      } else if (init && end === undefined) {
        const orderDate = new Date(order?.created_at)
        const marketDateStart = new Date(init.created_at)
        const isIn = orderDate.getTime() > marketDateStart.getTime()
        if (isIn) {
          isPromotedFreeShipment = true
        }
      }
    })
  }

  let shippingPriceValue = 0
  if (activeItems && activeItems.length > 0) {
    if (!isPromotedFreeShipment) {
      const marketplaceShipmentPrice = (subtotalValue < marketplace?.free_shipment_price) ? marketplace?.shipment_price || 0 : 0
      shippingPriceValue = order?.shipping_method.price + marketplaceShipmentPrice
    } else {
      shippingPriceValue = order?.shipping_method.price - Math.abs(marketplace?.shipment_price)
    }
  }

  shippingPriceValue = shippingPriceValue + shippingPriceToMadrid

  // ONLY FOR ECONOMY if flag FREE_SHIPMENT_ECONOMY is enabled set to 0 de shipment price
  const purchaseConditions = order?.purchase_conditions

  if (purchaseConditions !== null && purchaseConditions.free_vol === 'true') {
    if (purchaseConditions.free_vol_price_min && purchaseConditions.free_vol_price_min !== '' && subtotalValue >= Number(purchaseConditions.free_vol_price_min)) {
      // eslint-disable-next-line no-unused-vars
      const marketplaceShipmentPrice = (subtotalValue < marketplace?.free_shipment_price) ? marketplace?.shipment_price || 0 : 0
      // console.log(marketplaceShipmentPrice)
      shippingPriceValue = (purchaseConditions.shipping_price || 0)
    }
  }

  if (comeFromShipmentChange) {
    shippingPriceValue = order.shipping_price
  }

  if (order.status_id === PENALIZATION_STATUS) {
    shippingPriceValue = 0
  }
  const totalValue = total(activeItems, igicValue, shippingInsuranceValue, shippingPriceValue, shipMode, order?.discount, order?.wallet_amount_used) + ((isCustom) ? order?.custom_order_price : 0) + (order?.subscription_cost || 0)

  // Check if there was cuantity changes
  const hasCuantityChanges =
    subtotalValue !== order.subtotal ||
    synivaPriceValue !== order.syniva_price ||
    savingsValue !== order.total_saving ||
    shippingInsuranceValue !== order.shipping_insurance_price ||
    igicValue !== order.igic ||
    shippingPriceValue !== order.shipping_price ||
    totalValue !== order.total

  await supabaseClient
    .from('orders')
    .update({
      subtotal: subtotalValue,
      total_saving: savingsValue,
      syniva_price: synivaPriceValue,
      shipping_price: (order.status_id === PENALIZATION_STATUS) ? 0 : (comeFromShipmentChange) ? order.shipping_price : shippingPriceValue,
      shipping_insurance_price: shippingInsuranceValue,
      igic: igicValue,
      total: totalValue,
      updated_by: supabaseClient.auth.user()?.email,
      edited: !editable && hasCuantityChanges ? true : order.edited
    })
    .eq('id', orderId)

  await updateCalculatedTotalOrder(orderId)

  return {
    shippingInsurance: Math.abs(order.shipping_insurance_price - shippingInsuranceValue),
    igic: Math.abs(order.igic - igicValue)
  }
}

export const getUserAddresses = async (userId: number): Promise<[number | null, number | null]> => {
  const { data, error } = await supabaseClient
    .from('users')
    .select('default_shipping_address_id, default_billing_address_id')
    .eq('id', userId)
    .single()

  if (error) {
    return [null, null]
  }

  const shippingAddress = data.default_shipping_address_id || null
  const billingAddress = data.default_billing_address_id || null

  return [shippingAddress, billingAddress]
}

export const getShippingMethod = async (shippingMethodId: number | null) => {
  if (!shippingMethodId) {
    return null
  }

  const { data, error } = await supabaseClient
    .from('shipment_methods')
    .select('id, name, price')
    .eq('id', shippingMethodId)
    .single()

  if (error) {
    return null
  }

  return data
}

export const getNewSellers = async (sellersList: string[], marketplacesList: string[], orderId: number) => {
  try {
    const statusList = [
      PROCESSED_STATUS,
      RETURN_STATUS,
      SUPPLEMENT_STATUS,
      ADMITTED_STATUS,
      NO_STOCK_STATUS,
      PREPARED_STATUS,
      SHIPPED_STATUS,
      DELIVERED_STATUS,
      REFUND_STATUS,
      PARCIAL_RETURN_STATUS,
      DECREASE_RETURN_STATUS,
      INTERNAL_SPENDING_RETURN_STATUS
    ]

    const promiseSellerArray: any[] = []
    let sellersToCheck: any[] = []
    if (sellersList.length > 0) {
      for (const sList of sellersList) {
        promiseSellerArray.push(supabaseClient
          .from('suppliers')
          .select('id, legal_name, verified, seller_info, supplier_info_id(id, description)')
          .eq('seller', true)
          .eq('deleted', false)
          .ilike('legal_name', `%${sList.replace(/\n/g, '')}%`))
      }

      const resultsSellers = await Promise.all(promiseSellerArray)

      let querySellerSucess: any[] = []
      resultsSellers.forEach((r) => {
        if (r.data) {
          querySellerSucess = [...querySellerSucess, ...r.data]
        }
      })

      sellersToCheck = querySellerSucess.map((i) => {
        return {
          id: i.id,
          seller: i.legal_name,
          supplier_info_id: i.supplier_info_id,
          verified: i.verified,
          seller_info: i.seller_info
        }
      })
    }

    const promiseArray: any[] = []
    let marketsToCheck: any[] = []
    if (marketplacesList.length > 0) {
      for (const mList of marketplacesList) {
        promiseArray.push(supabaseClient
          .from('items')
          .select('id, seller, order_id(id, shipping_method_id, status_id)')
          .ilike('url', `%${mList}%`)
          .eq('marketplace_id', CUSTOM_ORDER_MARKETPLACE_ID)
          .not('order_id', 'is', null)
          .not('order_id.shipping_method_id', 'eq', ECONOMY_SHIPMENT_METHOD_ID)
          .not('order_id.id', 'eq', orderId)
          .in('order_id.status_id', statusList)
        )
      }

      const results = await Promise.all(promiseArray)

      let querySucess: any[] = []
      results.forEach((r) => {
        if (r.data) {
          querySucess = [...querySucess, ...r.data]
        }
      })

      marketsToCheck = querySucess.filter(i => i.order_id)
    }

    // console.log({ marketsToCheck, sellersToCheck })

    return { sellers: sellersToCheck, marketplaces: marketsToCheck }
  } catch (error) {
    console.log('Error fetching sellers items', (error as any).message)
    return null
  }
}

export const getShipmentData = async (orderId: any) => {
  if (!orderId) {
    return null
  }

  const { data, error } = await supabaseClient
    .from('shipments')
    .select('id, tracking_id, ticket_name, type, delivery_note, tracking_url, shipper_type')
    .eq('order_id', orderId)
    .single()

  if (error) {
    return null
  }

  return data
}

export const getOrderItems = async (orderId: number) => {
  if (!orderId) {
    return null
  }

  const { data, error } = await supabaseClient
    .from('items')
    .select('id, name, syniva_price, price, disabled')
    .eq('deleted', false)
    .eq('order_id', orderId)

  if (error) {
    return null
  }

  return data
}

export const prepareBIReport = async (dates: any) => {
  const { data, error } = await supabaseClient.rpc('bi_report_dates', { start_date: dates[0], end_date: dates[1] })
  if (error) {
    console.error(error)
    return { data: [], error }
  }
  return { data, error: null }
}

export const prepareBankReport = async (dates: any, returns: boolean) => {
  const { data, error } = await supabaseClient.rpc(returns ? 'banks_report_return_dates' : 'banks_report_dates', { start_date: dates[0], end_date: dates[1] })
  if (error) {
    console.error(error)
    return { data: [], error }
  }
  return { data, error: null }
}

export const prepareUnreviewedSales = async (dates: any) => {
  const { data, error } = await supabaseClient.rpc('get_number_of_inreviewed_sales', { p_gt_date: dates[0], p_lt_date: dates[1], p_admitted_status: ADMITTED_STATUS })
  if (error) {
    console.error(error)
    return { data: [], error }
  }
  return { data, error: null }
}

export const updateOrderAddresses = async (form: any, type: string, orderId: number) => {
  const islands = await getIslands()
  const postalCodeIsland = postalToIsland(islands, form?.form?.getFieldValue('postal_code'))

  if (postalCodeIsland === undefined) return { errorCode: 400, error: 'Island not found for the given postcode' }

  const dataAddress = {
    email: form?.form?.getFieldValue('email'),
    street: form?.form?.getFieldValue('street'),
    number: form?.form?.getFieldValue('number'),
    city: form?.form?.getFieldValue('city'),
    postal_code: form?.form?.getFieldValue('postal_code'),
    name: form?.form?.getFieldValue('name'),
    nif: form?.form?.getFieldValue('nif'),
    phone_number: form?.form?.getFieldValue('phone_number'),
    surnames: form?.form?.getFieldValue('surnames'),
    type: type,
    additional_info: form?.initialValues?.additional_info,
    is_company: form?.initialValues?.is_company,
    island_id: postalCodeIsland?.id,
    user_id: form?.initialValues?.user_id
  }

  const { data: user, error: userError } = await supabaseClient
    .from('users')
    .select('id, default_billing_address_id, default_shipping_address_id')
    .eq('id', dataAddress.user_id)

  if (userError) {
    return { error: userError }
  }

  let newAddr: any
  let orderUpdated: any

  if (user) {
    // Create/Edit the new address
    if (user[0].default_billing_address_id === form?.initialValues?.id || user[0]?.default_shipping_address_id === form?.initialValues?.id) {
      newAddr = await supabaseClient
        .from('addresses')
        .insert(dataAddress)
    } else {
      newAddr = await supabaseClient
        .from('addresses')
        .update(dataAddress)
        .eq('id', form?.initialValues?.id)
    }

    // Update the order
    if (newAddr.data !== null) {
      if (type === 'shipping') {
        orderUpdated = await supabaseClient
          .from('orders').update({
            shipping_address_id: newAddr.data[0].id,
            updated_by: supabaseClient.auth.user()?.email
          })
          .eq('id', orderId)
      } else if (type === 'billing') {
        orderUpdated = await supabaseClient
          .from('orders').update({
            billing_address_id: newAddr.data[0].id,
            updated_by: supabaseClient.auth.user()?.email
          })
          .eq('id', orderId)
      }
    } else {
      return { error: newAddr.error }
    }
    if (orderUpdated.data) {
      return { dataAddress: dataAddress, newAddr: newAddr }
    } else {
      return { error: orderUpdated.error }
    }
  }
}

export const generateHorizontalAudit = async (dates: Date[]) => {
  // Retrieve information about orders and prepare a report
  const auditArrayPromises = []

  auditArrayPromises.push(supabaseClient.from('audit_orders').select('created_at, record, resource_id').order('created_at', { ascending: true }).gte('created_at', dates[0]).lte('created_at', dates[1]))
  auditArrayPromises.push(supabaseClient.from('audit_shipments').select('created_at, record, order_id').order('created_at', { ascending: true }).gte('created_at', dates[0]).lte('created_at', dates[1]))
  auditArrayPromises.push(supabaseClient.from('audit_market_orders').select('created_at, record, order_id').order('created_at', { ascending: true }).gte('created_at', dates[0]).lte('created_at', dates[1]))
  auditArrayPromises.push(supabaseClient.from('audit_supplements').select('created_at, record, order_id').order('created_at', { ascending: true }).gte('created_at', dates[0]).lte('created_at', dates[1]))

  const data = await Promise.all(auditArrayPromises)

  // dataConcatenated = [].concat.apply([], data.map(x => x.data))

  const shipmentProcessedList: any[] = []
  const moProcessedList: any[] = []
  const supplementsProcessedList: any[] = []

  const finalDataList: any[] = []

  let insertedByOrder = 0
  const processedList: any[] = []

  if (data && data[0]) {
    const orders = data[0]

    orders?.data?.forEach(o => {
      if (!processedList.includes(o.resource_id)) {
        let rowOrderInfo = {
          mo_date: '',
          marketplace: '',
          purchase_amount: 0,
          sup_date: '',
          transport_pending: '',
          transport_admitted: '',
          transportSupplement_pending: '',
          transportSupplement_admitted: '',
          price_pending: '',
          price_admitted: '',
          iva_pending: '',
          iva_admitted: '',
          other_pending: '',
          other_admitted: '',
          dua_pending: '',
          dua_admitted: '',
          lowValue_pending: '',
          lowValue_admitted: '',
          tariff_pending: '',
          tariff_admitted: '',
          igic_pending: '',
          igic_admitted: '',
          shipping_insurance_pending: '',
          shipping_insurance_admitted: '',
          vol_pending: '',
          vol_admitted: '',
          amount: 0,
          shipment_method: '',
          shipper_id: ''
        }
        const orderGroup = orders.data.filter(x => x.resource_id === o.resource_id)
        if (orderGroup.length > 0) {
          // Prepare data for shipment
          orderGroup.forEach(order => {
            const currentOrder = order.record

            const orderRow = {
              id: currentOrder.id,
              status: currentOrder.status_id,
              device: currentOrder?.device || '',
              user_id: currentOrder.user_id,
              return_request: currentOrder.return_date,
              cancel_request: currentOrder.cancelation_date,
              pending_payment: null,
              requested: null,
              processed: null,
              cancelled: null,
              returned: null,
              supplement: null,
              admitted: null,
              no_stock: null,
              prepared: null,
              sent: null,
              delivered: null,
              penalty: null,
              credit: null,
              mo_date: '',
              marketplace: '',
              purchase_amount: 0
            }

            if (currentOrder.status_id === 0) {
              orderRow.pending_payment = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 1) {
              orderRow.requested = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 2) {
              orderRow.processed = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 3) {
              orderRow.cancelled = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 4) {
              orderRow.returned = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 5) {
              orderRow.supplement = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 6) {
              orderRow.admitted = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 7) {
              orderRow.no_stock = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 8) {
              orderRow.prepared = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 9) {
              orderRow.sent = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 10) {
              orderRow.delivered = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 11) {
              orderRow.penalty = currentOrder.updated_at || currentOrder.created_at
            } else if (currentOrder.status_id === 12) {
              orderRow.credit = currentOrder.updated_at || currentOrder.created_at
            }

            rowOrderInfo = Object.assign({}, rowOrderInfo, removeNull(orderRow))
          })
        }

        // market_orders
        const marketOrders = data[2]?.data?.filter(x => x.order_id === o.resource_id)
        if (marketOrders && marketOrders.length > 0) {
          // Prepare data for market orders
          marketOrders.forEach(mo => {
            const currentMarketOrder = mo.record
            if (!moProcessedList.includes(currentMarketOrder.id) && currentMarketOrder.deleted_by === null) {
              // console.log(`Processing market_order ${currentMarketOrder.id} data for order ${rowOrderInfo.id}`)

              const rowMarketOrder = Object.assign({
                mo_date: '',
                marketplace: '',
                purchase_amount: 0
              }, rowOrderInfo)
              rowOrderInfo.mo_date = rowMarketOrder.mo_date = currentMarketOrder.created_at
              rowOrderInfo.marketplace = rowMarketOrder.marketplace = (currentMarketOrder.marketplace_legal_name) ? currentMarketOrder.marketplace_legal_name : ''
              rowOrderInfo.purchase_amount = rowMarketOrder.purchase_amount = currentMarketOrder.purchase_amount
              finalDataList.push(rowMarketOrder)
              moProcessedList.push(currentMarketOrder.id)
              insertedByOrder += 1
            }
          })
        }

        // market_orders
        const supplements = data[3]?.data?.filter(x => x.order_id === o.resource_id)
        if (supplements && supplements.length > 0) {
          // Prepare data for shipment
          supplements.forEach(sup => {
            const currentSupplement = sup.record
            if (!supplementsProcessedList.includes(currentSupplement.id) && currentSupplement.deleted === false) {
              // console.log(`Processing suplements ${currentSupplement.id} data for order ${rowOrderInfo.id}`)

              const rowSupplement = Object.assign({
                sup_date: '',
                transport_pending: '',
                transport_admitted: '',
                transportSupplement_pending: '',
                transportSupplement_admitted: '',
                price_pending: '',
                price_admitted: '',
                iva_pending: '',
                iva_admitted: '',
                other_pending: '',
                other_admitted: '',
                dua_pending: '',
                dua_admitted: '',
                lowValue_pending: '',
                lowValue_admitted: '',
                tariff_pending: '',
                tariff_admitted: '',
                igic_pending: '',
                igic_admitted: '',
                shipping_insurance_pending: '',
                shipping_insurance_admitted: '',
                vol_pending: '',
                vol_admitted: '',
                amount: 0
              }, rowOrderInfo)
              rowOrderInfo.sup_date = rowSupplement.sup_date = currentSupplement.created_at
              rowOrderInfo.transport_pending = rowSupplement.transport_pending = (currentSupplement.description === 'transportToPeninsula' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.transport_admitted = rowSupplement.transport_admitted = (currentSupplement.description === 'transportToPeninsula' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.transportSupplement_pending = rowSupplement.transportSupplement_pending = (currentSupplement.description === 'transportSupplement' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.transportSupplement_admitted = rowSupplement.transportSupplement_admitted = (currentSupplement.description === 'transportSupplement' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.price_pending = rowSupplement.price_pending = (currentSupplement.description === 'priceChange' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.price_admitted = rowSupplement.price_admitted = (currentSupplement.description === 'priceChange' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.iva_pending = rowSupplement.iva_pending = ((currentSupplement.description === 'reducedIva' || currentSupplement.description === 'reducedIvaSecondHand' || currentSupplement.description === 'reducedIvaBreakdown' || currentSupplement.description === 'reducedIvaEU' || currentSupplement.description === 'reducedIvaShipment') && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.iva_admitted = rowSupplement.iva_admitted = ((currentSupplement.description === 'reducedIva' || currentSupplement.description === 'reducedIvaSecondHand' || currentSupplement.description === 'reducedIvaBreakdown' || currentSupplement.description === 'reducedIvaEU' || currentSupplement.description === 'reducedIvaShipment') && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.other_pending = rowSupplement.other_pending = (currentSupplement.description === 'other' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.other_admitted = rowSupplement.other_admitted = (currentSupplement.description === 'other' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.dua_pending = rowSupplement.dua_pending = (currentSupplement.description === 'dua' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.dua_admitted = rowSupplement.dua_admitted = (currentSupplement.description === 'dua' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.lowValue_pending = rowSupplement.lowValue_pending = (currentSupplement.description === 'lowValue' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.lowValue_admitted = rowSupplement.lowValue_admitted = (currentSupplement.description === 'lowValue' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.tariff_pending = rowSupplement.tariff_pending = (currentSupplement.description === 'tariff' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.tariff_admitted = rowSupplement.tariff_admitted = (currentSupplement.description === 'tariff' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.igic_pending = rowSupplement.igic_pending = (currentSupplement.description === 'igic' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.igic_admitted = rowSupplement.igic_admitted = (currentSupplement.description === 'igic' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.shipping_insurance_pending = rowSupplement.shipping_insurance_pending = (currentSupplement.description === 'shippingInsurancePrice' && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.shipping_insurance_admitted = rowSupplement.shipping_insurance_admitted = (currentSupplement.description === 'shippingInsurancePrice' && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.vol_pending = rowSupplement.vol_pending = (currentSupplement.description === null && currentSupplement.status_id === 0) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.vol_admitted = rowSupplement.vol_admitted = (currentSupplement.description === null && currentSupplement.status_id === 6) ? currentSupplement.updated_at || currentSupplement.created_at : ''
              rowOrderInfo.amount = rowSupplement.amount = currentSupplement.supplement
              finalDataList.push(rowSupplement)
              supplementsProcessedList.push(currentSupplement.id)
              insertedByOrder += 1
            }
          })
        }

        // shipments
        const shipments = data[1]?.data?.filter(x => x.order_id === o.resource_id).sort((prev, next) => Date.parse(next.created_at) - Date.parse(prev.created_at))
        if (shipments && shipments.length > 0) {
          // Prepare data for shipment
          // order by created_at descending
          shipments.forEach(ship => {
            const currentShipment = ship.record
            if (!shipmentProcessedList.includes(currentShipment.id)) {
              // console.log(`Processing Shipment ${currentShipment.id} data for order ${rowOrderInfo.id}`)

              const rowShipment = Object.assign({
                shipment_method: '',
                shipper_id: ''
              }, rowOrderInfo)
              rowOrderInfo.shipment_method = rowShipment.shipment_method = currentShipment.shipper_type
              rowOrderInfo.shipper_id = rowShipment.shipper_id = (currentShipment.shipper_type?.toLowerCase().includes('correos')) ? currentShipment.tracking_id : TIPSA_AGENCY_CODE + (currentShipment?.type?.toLowerCase().includes('directo') ? TIPSA_AGENCY_SERVICE_TYPE_DIRECT : TIPSA_AGENCY_SERVICE_TYPE_CONSOLIDATED) + currentShipment.delivery_note
              finalDataList.push(rowShipment)
              shipmentProcessedList.push(currentShipment.id)
              insertedByOrder += 1
            }
          })
        }

        if (insertedByOrder <= 0) {
          finalDataList.push(rowOrderInfo)
        }
        processedList.push(o.resource_id)
        insertedByOrder = 0
      }
    })

    // Fix the dates and calculate days between states
    finalDataList.forEach((row, index) => {
      // Provider transit: days between processed status and admitted
      let d1 = new Date(row.processed)
      let d2 = new Date(row.admitted)
      if (d1 && d2) {
        row.prov_transit = ''
        const transitProviderDays = Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24))
        if (!Number.isNaN(transitProviderDays)) {
          row.prov_transit = transitProviderDays.toString()
        }
      }

      // syniva management: days between admitted status and sent status
      d1 = new Date(row.admitted)
      d2 = new Date(row.sent)
      if (d1 && d2) {
        row.syniva_transit = ''
        const transitSynivaDays = Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24))
        if (!Number.isNaN(transitSynivaDays)) {
          row.syniva_transit = transitSynivaDays.toString()
        }
      }

      // Provider sent: days between sent status and delivered status
      d1 = new Date(row.sent)
      d2 = new Date(row.delivered)
      if (d1 && d2) {
        row.syniva_sent = ''
        const sentSynivaDays = Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24))
        if (!Number.isNaN(sentSynivaDays)) {
          row.syniva_sent = sentSynivaDays.toString()
        }
      }

      // full sent: days between resquested status and delivered status
      d1 = new Date(row.requested)
      d2 = new Date(row.delivered)
      if (d1 && d2) {
        row.full_sent = ''
        const fullSentDays = Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 3600 * 24))
        if (!Number.isNaN(fullSentDays)) {
          row.full_sent = fullSentDays.toString()
        }
      }

      if (row.status >= 0) {
        row.status = MapStatusIDToText(row.status)
      }

      if (index > 0) {
        row.iva_admitted = formatDateTime(row.iva_admitted, true)
        row.iva_pending = formatDateTime(row.iva_pending, true)
        row.mo_date = formatDateTime(row.mo_date, true)
        row.other_admitted = formatDateTime(row.other_admitted, true)
        row.other_pending = formatDateTime(row.other_pending, true)
        row.dua_admitted = formatDateTime(row.dua_admitted, true)
        row.dua_pending = formatDateTime(row.dua_pending, true)
        row.lowValue_admitted = formatDateTime(row.lowValue_admitted, true)
        row.lowValue_pending = formatDateTime(row.lowValue_pending, true)
        row.tariff_admitted = formatDateTime(row.tariff_admitted, true)
        row.tariff_pending = formatDateTime(row.tariff_pending, true)
        row.igic_admitted = formatDateTime(row.igic_admitted, true)
        row.igic_pending = formatDateTime(row.igic_pending, true)
        row.shipping_insurance_pending = formatDateTime(row.shipping_insurance_pending, true)
        row.shipping_insurance_admitted = formatDateTime(row.shipping_insurance_admitted, true)
        row.price_admitted = formatDateTime(row.price_admitted, true)
        row.price_pending = formatDateTime(row.price_pending, true)
        row.requested = formatDateTime(row.requested, true)
        row.sup_date = formatDateTime(row.sup_date, true)
        row.transport_admitted = formatDateTime(row.transport_admitted, true)
        row.transport_pending = formatDateTime(row.transport_pending, true)
        row.vol_admitted = formatDateTime(row.vol_admitted, true)
        row.vol_pending = formatDateTime(row.vol_pending, true)
        row.pending_payment = formatDateTime(row.pending_payment, true)
        row.processed = formatDateTime(row.processed, true)
        row.cancelled = formatDateTime(row.cancelled, true)
        row.returned = formatDateTime(row.returned, true)
        row.supplement = formatDateTime(row.supplement, true)
        row.admitted = formatDateTime(row.admitted, true)
        row.no_stock = formatDateTime(row.no_stock, true)
        row.prepared = formatDateTime(row.prepared, true)
        row.sent = formatDateTime(row.sent, true)
        row.delivered = formatDateTime(row.delivered, true)
        row.penalty = formatDateTime(row.penalty, true)
        row.credit = formatDateTime(row.credit, true)
      }
    })

    return finalDataList
  }
  return []
}

// All users and All info
export const getAllUsers = async (onProgress?: (progress: number) => void, filters: filter[] = []) => {
  onProgress?.(0)

  let countQuery = supabaseClient.from('users')
    .select('id', { count: 'exact' })
    .gt('id', 0)
    .eq('deleted', false)

  countQuery = applyFilterToQuery(countQuery, filters)

  const { count } = await countQuery

  let maxIterations = Math.floor((count || 0) / MAX_ROWS_QUERY)

  if ((count || 0) % MAX_ROWS_QUERY > 0) {
    maxIterations += 1
  }

  onProgress?.(5)

  const iterateArray = new Array(maxIterations)
  const usersArray = []

  let offset = 0
  let currentIteration = 0
  // eslint-disable-next-line no-unused-vars
  for (const index of iterateArray) {
    currentIteration++
    const progressPercent = Math.round(5 + (currentIteration / maxIterations) * 90)
    onProgress?.(progressPercent)

    const select = `id, email, name, wallet_balance, role_id(name), created_at, default_shipping_address_id(name, surnames, nif),
        referred_by_user_id(referral_benefits!referrer_user_id(id, benefit_amount)),
        referrer_benefits:referral_benefits!referrer_user_id(id, order_id, status, benefit_amount, created_at, referred_expired_at, referrer_user_id(id, name), referred_user_id(id, name)),
        referred_benefits:referral_benefits!referred_user_id(id, order_id, status, benefit_amount, created_at, referred_expired_at, processed_promo, referrer_user_id(id, name), referred_user_id(id, name)),
        orders!user_id(id, status_id, wallet_amount_used, total, created_at, atypical, user_id, shipping_price, marketplace_id(id, free_shipment_price, shipment_price), items(id, shipping_price_to_madrid), syniva_price, shipping_insurance_price, custom_order_price, supplements(id, volumetric, deleted, supplement))`

    let catchTrigger = false
    try {
      let usersQuery = supabaseClient.from('users')
        .select(select)
        .gt('id', 0)
        .eq('deleted', false)
        .range(offset, offset + MAX_ROWS_QUERY - 1)

      usersQuery = applyFilterToQuery(usersQuery, filters)

      const { data: users, error: userError } = await usersQuery.throwOnError(true)

      if (userError) continue

      usersArray.push(users)
      offset += MAX_ROWS_QUERY
    } catch (error) {
      catchTrigger = true
      console.error({ error: 'Error on users export query' })
      onProgress?.(progressPercent)
    }

    if (catchTrigger) {
      const MAX_SUBROWS_QUERY = MAX_ROWS_QUERY / 20

      const subrange = [offset, offset + (MAX_SUBROWS_QUERY - 1)]
      const promiseSubRangeArray: any[] = []
      for (let indexSubrange = 0; indexSubrange < 20; indexSubrange++) {
        let subQuery = supabaseClient.from('users')
          .select(select)
          .gt('id', 0)
          .eq('deleted', false)
          .range(subrange[0], subrange[1])

        subQuery = applyFilterToQuery(subQuery, filters)

        promiseSubRangeArray.push(subQuery)

        subrange[0] += MAX_SUBROWS_QUERY
        subrange[1] += MAX_SUBROWS_QUERY
      }

      let usersRetry: any[] = []

      for (const indexRange of promiseSubRangeArray) {
        const usersSubRange = await indexRange
        usersRetry = [...usersRetry, ...usersSubRange.data]
      }
      usersArray.push(usersRetry)
    }
  }

  onProgress?.(95)

  let querySucess = [] as any[]
  usersArray.forEach((r) => {
    if (r) {
      querySucess = [...querySucess, ...r]
    }
  })

  onProgress?.(100)

  return querySucess
}

export const getUsersWithoutOrdersFunction = async (dates: any) => {
  const { data, error } = await supabaseClient.rpc('get_users_without_orders', { start_date: dates[0], end_date: dates[1] })

  if (error) {
    console.error(error)
    return []
  }
  return data || []
}

export const getUsersByDateFunction = async (dates: any) => {
  const { data, error } = await supabaseClient.rpc('get_users_by_date', { start_date: dates[0], end_date: dates[1] })

  if (error) {
    console.error(error)
    return []
  }
  return data || []
}

export const getAllMarketplaces = async () => {
  const { count } = await supabaseClient.from('marketplaces')
    .select('id', { count: 'exact', head: true })
    .eq('deleted', false)

  let maxIterations = Math.floor((count || 0) / MAX_ROWS_SALES)

  if ((count || 0) % MAX_ROWS_SALES > 0) {
    maxIterations += 1
  }

  const iterateArray = new Array(maxIterations)
  const promiseArray = []

  const range = [0, MAX_ROWS_SALES - 1]
  // eslint-disable-next-line no-unused-vars
  for (const index of iterateArray) {
    promiseArray.push(await supabaseClient.from('marketplaces')
      .select('id, name, logo_url, description, status, url, is_in_home, extra_info, is_in_app, shipment_price, free_shipment_price, weight, iva_percent, created_at, updated_at, original_url, is_custom_shop,' +
      'igic_types(id, name, value),' +
      'marketplace_category_relations(' +
      'marketplace_categories(name)), return_days')
      .eq('deleted', false)
      .range(range[0], range[1]))
    range[0] += MAX_ROWS_SALES
    range[1] += MAX_ROWS_SALES
  }

  const results = await Promise.all(promiseArray)

  let querySucess = [] as any[]
  results.forEach((r) => {
    if (r.data) {
      querySucess = [...querySucess, ...r.data]
    }
  })

  return querySucess
}

export const getGroupedMarketplace = async (siteName: string) => {
  const dataToReturn: any = {
    internal: [],
    related: []
  }

  const promiseArray = [
    supabaseClient.from('marketplaces')
      .select('id, name, is_custom_shop, extra_info')
      .eq('is_custom_shop', false)
      .eq('deleted', false)
      .ilike('name', `${siteName.toLowerCase()}`),
    supabaseClient.from('marketplaces')
      .select('id, name, is_custom_shop, extra_info, internal_shop_id')
      .eq('is_custom_shop', true)
      .eq('deleted', false)
      .ilike('name', `${siteName.toLowerCase()}`)
  ]

  const results = await Promise.all(promiseArray)

  results.forEach((r: any, index: number) => {
    if (index === 0) {
      dataToReturn.internal = r.data[0]
    } else {
      dataToReturn.related = r.data
    }
  })

  return dataToReturn
}

export const registerAccess = async (operation: string, userId: string, email: string | null) => {
  const { data: registry, error: accessError } = await supabaseClient.from('audit_user_access')
    .insert({
      operation: operation,
      resource_id: userId,
      email: email
    }).single()

  if (accessError) {
    console.log(accessError)
  }

  await supabaseClient.from('users')
    .update({
      last_access: registry?.created_at
    })
    .eq('supabase_user_id', userId)
}

export const updateIssueDeadlines = (marketplaceId: number) => {
  fetch(`${API_URL}/actions/issues/updateIssueDeadlines/${marketplaceId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

export const checkIssueDeadline = async (orderId: number, issueId?: number) => {
  const { data: supData } = await supabaseClient.from('supplements').select('id, created_at')
    .eq('order_id', orderId)
    .eq('volumetric', true)
    .eq('deleted', false)
    .single()

  const { data: orderData, error: orderError } = await supabaseClient.from('orders').select('id, marketplace_id(id, return_days), custom_marketplace_id(id, return_days)')
    .eq('id', orderId)
    .single()

  if (orderError) {
    console.log({ msg: `Error fetching order ${orderId}`, error: orderError.message })
  }

  let issueDeadline = null
  const returnDays = orderData?.marketplace_id?.id ? orderData?.marketplace_id?.return_days : orderData?.custom_marketplace_id?.return_days

  if (orderData && supData) {
    if (supData?.created_at) {
      issueDeadline = getReturnDayLimit(supData.created_at, returnDays ?? MAX_RETURN_DEADLINE)
      if (issueId) {
        await supabaseClient.from('issues').update({
          issue_deadline: issueDeadline.toISOString()
        }).eq('id', issueId)
      } else {
        await supabaseClient.from('issues').update({
          issue_deadline: issueDeadline.toISOString()
        }).eq('order_id', orderId)
      }
    }
  }
}

export const updateIssueLocation = async (key: number | null | undefined, issueId: number) => {
  await supabaseClient.from('issues').update({
    location: key,
    updated_by: supabaseClient.auth.user()?.email
  })
    .eq('id', issueId)
}

export const updateRefundStatusOnAdmit = async (record: definitions['refunds']) => {
  // If the refund to pass is unified, admite all it sons
  if (record.description === 'Unificado') {
    await supabaseClient.from('refunds').update({ status_id: ADMITTED_STATUS }).eq('parent_supplement_id', record.supplement_id)
  // If is an initial payment, pass all te refunds of it's refund_invoice
  } else if (record.description === 'Pago inicial' && record.refund_invoice) {
    await supabaseClient.from('refunds').update({ status_id: ADMITTED_STATUS }).eq('refund_invoice', record.refund_invoice)
  }
}

export const addTransportSupplementOrRefund = async (orderId: number, description: string, statusId: number, supAmount: number) => {
  await supabaseClient.from('supplements')
    .insert({
      updated_by: supabaseClient.auth.user()?.email,
      order_id: orderId,
      description: description,
      supplement: supAmount.toFixed(2),
      status_id: statusId,
      volumetric: false,
      deleted: (supAmount === 0)
    })
}

const applySorterToQuery = (query: PostgrestFilterBuilder<any>, sorter: sorter) => {
  return query.order(sorter.field, { ascending: sorter.ascending })
}

const applyFilterToQuery = (query: PostgrestFilterBuilder<any>, filters: filter[]) => {
  filters.forEach((f: filter) => {
    switch (f.operator) {
      case 'eq':
        query.eq(f.field, f.value)
        break
      case 'gt':
        query.gt(f.field, f.value)
        break
      case 'gte':
        query.gte(f.field, f.value)
        break
      case 'lt':
        query.lt(f.field, f.value)
        break
      case 'lte':
        query.lte(f.field, f.value)
        break
      case 'in':
        query.in(f.field, f.value)
        break
      case 'not':
        query.not(f.field, f.secondOp as any, f.value)
        break
      case 'is':
        query.is(f.field, f.value)
        break
      case 'ilike':
        query.ilike(f.field, `%${f.value}%`)
        break
      case 'like':
        query.like(f.field, `%${f.value}%`)
        break
    }
  })
  return query
}

export const getSupaData = async (resource: string, select: string, cPage: number, cPageSize: number, filters: filter[], sorter: sorter) => {
  let counter = 0

  let supabaseCounterQuery = supabaseClient.from(resource)
    .select(`${select}`, { count: 'exact', head: true })

  supabaseCounterQuery = applyFilterToQuery(supabaseCounterQuery, filters)

  const { count } = await supabaseCounterQuery
  counter = count || 0

  let maxIterations = Math.floor((counter || 0) / MAX_ROWS_SALES)

  if ((counter || 0) % MAX_ROWS_SALES > 0) {
    maxIterations += 1
  }

  const iterateArray = new Array(maxIterations)
  const promiseArray = []

  const range = [0, MAX_ROWS_SALES - 1]
  // eslint-disable-next-line no-unused-vars
  for (const index of iterateArray) {
    let supabaseDataQuery = supabaseClient.from(resource).select(select)

    supabaseDataQuery = applyFilterToQuery(supabaseDataQuery, filters)
    supabaseDataQuery = applySorterToQuery(supabaseDataQuery, sorter)

    promiseArray.push(supabaseDataQuery.range(range[0], range[1]))
    range[0] += MAX_ROWS_SALES
    range[1] += MAX_ROWS_SALES
  }

  const results = await Promise.all(promiseArray)

  let querySucess: any[] = []
  results.forEach((r) => {
    if (r.data) {
      querySucess = [...querySucess, ...r.data]
    }
  })
  return { data: querySucess, count: counter }
}

export const getReferredBenefits = async (id: number) => {
  const { data } = await supabaseClient
    // eslint-disable-next-line camelcase
    .from<definitions['referral_benefits'] & { referred_user_id: definitions['users'] } & { referrer_user_id: definitions['users'] }>('referral_benefits')
    .select('status, benefit_amount, created_at, referred_expired_at, referrer_user_id(id, name), referred_user_id(id, name), promo_id, processed_promo')
    .eq('referred_user_id', id)

  return data || []
}

export const getReferrerBenefits = async (id: number) => {
  const { data } = await supabaseClient
    // eslint-disable-next-line camelcase
    .from<definitions['referral_benefits'] & { referred_user_id: definitions['users'] }>('referral_benefits')
    .select('status, benefit_amount, created_at, referrer_expired_at, referred_expired_at, referred_user_id(name), order_id(id, total)')
    .eq('referrer_user_id', id)

  return data || []
}

export const getBenefitReferrerMinOrder = async () => {
  const { data } = await supabaseClient
    .from<definitions['variables']>('variables')
    .select('value')
    .eq('name', VAR_BENEFIT_MIN_ORDER)
    .single()

  return data
}

export const getOrdersWithWalletAmountUsed = async (id: number) => {
  const { data } = await supabaseClient
    // eslint-disable-next-line camelcase
    .from<definitions['orders'] & { user_id: definitions['users'] }>('orders')
    .select('id, status_id, wallet_amount_used, created_at, user_id(name)')
    .eq('user_id', id)
    .gt('wallet_amount_used', 0)
    .gt('status_id', ORDER_STATUS_ID_PENDING_PAYMENT)

  return data || []
}

export const findPendingMarketOrdersWithoutInvoice = async (value: string) => {
  const { data } = await supabaseClient
    .rpc('filter_pending_market_orders', { value })

  return data
}

export const cloneOrderItems = async (orderFrom: number, orderTo: number, userId: number, marketplaceId: number) => {
  // Check if the invoice is editable before making changes
  const { editable } = await checkIfInvoiceIsEditable(orderTo)

  // check if is the same user_id and if from have items and to not
  const { data: orders, error: errorOrders } = await supabaseClient.from('orders')
    .select('id, user_id, items(*), marketplace_id')
    .in('id', [orderFrom, orderTo])
    .eq('user_id', userId)
    .eq('marketplace_id', marketplaceId)

  if (errorOrders) {
    return { success: false, error: true }
  } else if (orders.length <= 1) {
    return { success: false, error: true }
  }

  const orderF = orders.find((x: any) => x.id === orderFrom)
  const orderT = orders.find((x: any) => x.id === orderTo)

  if (orderT.items && orderT.items.length > 0) {
    return { success: false, error: true }
  }

  if (orderF.items && orderF.items.length <= 0) {
    return { success: false, error: true }
  }

  // clone the items
  const itemsArray = [...orderF.items]
  itemsArray.forEach((i: any) => {
    i.order_id = orderTo
    i.updated_by = supabaseClient.auth.user()?.email
    delete i.id
  })

  const { data: items, error: errorItems } = await supabaseClient.from('items')
    .insert(itemsArray)

  if (errorItems) {
    return { success: false, error: true }
  }

  // Always update the order with edited = true if not editable
  if (!editable) {
    const { error: errorUpdate } = await supabaseClient.from('orders')
      .update({
        edited: true,
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('id', orderTo)

    if (errorUpdate) {
      return { success: false, error: true, message: 'Failed to update order with edited = true' }
    }
  }

  return { success: true, error: false, data: items }
}

// Profile
export const getLastEntryData = async (userId: number) => {
  const { data, error } = await supabaseClient.from<definitions['employee_times']>('employee_times')
    .select('*')
    .eq('user_id', userId)
    .is('date_end', null)
    .order('created_at', { ascending: false })
    .limit(1)

  if (error) {
    console.log(error)
    return null
  }

  return (data && data[0]) ? data[0] as definitions['employee_times'] : null
}

export const getAllEntryDatas = async (userId: number) => {
  const { data, error } = await supabaseClient.from<definitions['employee_times']>('employee_times')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.log(error)
    return null
  }

  return data
}

export const upsertEmployeeTime = async (userId: number, clockInDate?: any, entryDate?: Date, note?: string, entryId?: number) => {
  const currentDate = clockInDate !== null ? clockInDate : new Date(Date.now())

  let totalHours = null
  let dateStart = currentDate
  let dateEnd: Date | undefined

  if (entryId !== undefined) {
    dateEnd = currentDate
    dateStart = entryDate || currentDate
    totalHours = dateEnd!.getTime() - dateStart.getTime()
  }

  const info = {
    id: entryId || undefined,
    user_id: userId,
    updated_at: currentDate.toISOString(),
    updated_by: supabaseClient.auth.user()?.email,
    note: note || undefined,
    date_start: dateStart.toISOString(),
    date_end: dateEnd ? dateEnd.toISOString() : undefined,
    total: totalHours || undefined
  }

  const { data, error } = await supabaseClient.from<definitions['employee_times']>('employee_times').upsert(info)

  if (error) {
    console.log(error)
    return null
  }
  return (data && data[0]) ? data[0] as definitions['employee_times'] : null
}

export const updateEmployeeTime = async (employeeTimeId: number, clockInStartDate: any, clockInEndDate: any) => {
  const totalHours = clockInEndDate.getTime() - clockInStartDate.getTime()

  const info = {
    updated_at: new Date(Date.now()).toISOString(),
    updated_by: supabaseClient.auth.user()?.email,
    date_start: clockInStartDate,
    date_end: clockInEndDate,
    total: totalHours
  }

  const { data, error } = await supabaseClient.from<definitions['employee_times']>('employee_times').update(info).eq('id', employeeTimeId)

  if (error) {
    console.log(error)
    return null
  }
  return (data && data[0]) ? data[0] as definitions['employee_times'] : null
}

export const getEmployeeData = async (userId: number, timesFilter?: CrudFilters[], absentFilters?: CrudFilters[]) => {
  const currentDate = new Date(Date.now())
  currentDate.setMonth(currentDate.getMonth() - 1)

  const [userResponse] = await Promise.all([
    supabaseClient.from('users')
      .select('*')
      .eq('id', userId)
      .single()
  ])

  const user = userResponse.data

  return { user }
}

export const getLastAuditOrderWithProcessedStatus = async (ordersId: number[]) => {
  const { data: auditOrder, error } = await supabaseClient.from('audit_orders')
    .select('id, record, resource_id, created_at')
    .in('resource_id', ordersId)
    .eq('record->>status_id', PROCESSED_STATUS)
    .order('created_at', { ascending: true }) // TODO: change to descending

  if (error) {
    console.log(error)
    return { data: null, error }
  }

  return { data: auditOrder, error: null }
}

export const getOrdersWithProcessedStatusBeforeDate = async (date: Date) => {
  const { data: processedOrders, error: errorProcessed } = await supabaseClient.from('orders')
    .select('id, status_id')
    .eq('status_id', PROCESSED_STATUS)

  if (errorProcessed) {
    console.log(errorProcessed)
    return { data: null, errorProcessed }
  }

  const { data: auditOrder, error } = await supabaseClient.from('audit_orders')
    .select('id, created_at, resource_id')
    .lte('created_at', date.toISOString())
    .in('resource_id', processedOrders?.map((o) => o.id) || [])
    .eq('record->>status_id', PROCESSED_STATUS)
    .order('created_at', { ascending: true })

  if (error) {
    console.log(error)
    return { data: null, error }
  }

  return { data: auditOrder, error: null }
}

export const getSignedUrl = async (value: string, folder?: boolean) => {
  // https://ddabbwwudcyhcorwvwwt.supabase.co/storage/v1/object/public/files/F-test.pdf
  let bucket = ''
  let filename = ''
  if (folder) {
    const indexPublic = value.indexOf('/public/')

    if (indexPublic === -1) return null

    const pathValue = value.slice(indexPublic + 8)

    const splittedValue = pathValue.split('/')
    bucket = splittedValue[0]

    // If the path has more than 2 segments, it means that the file is in a subfolder
    const pathFileValue = splittedValue.length > 2
      ? pathValue.slice(value.indexOf('/') + 1)
      : splittedValue.slice(1).join('/')
    filename = pathFileValue
  } else {
    const splittedValue = value.split('/')
    bucket = splittedValue[splittedValue.length - 2]
    filename = splittedValue[splittedValue.length - 1]
  }

  const { signedURL, error } = await supabaseClient.storage.from(bucket).createSignedUrl(filename, 60)

  if (error) return null

  return signedURL
}

export async function getSubscriptions () {
  const { data } = await supabaseClient
    .from('subscriptions')
    .select('*')

  if (data) return data
  else return null
}

export async function getActiveUserSubscriptions (userId: number) {
  const { data } = await supabaseClient
    .from('users_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'active')

  if (data) return data
  else return null
}

export async function getRefundInvoicesByOrderId (orderId: number | undefined) {
  const { data, error } = await supabaseClient
    .from('refunds_invoices')
    .select('*')
    .eq('order_id', orderId)

  if (error) {
    console.log(error)
    return null
  } else {
    console.log({ data })
    return data
  }
}

export async function checkIfInvoiceIsEditable (orderId: number | undefined) {
  const { data, error } = await supabaseClient
    .from('invoices')
    .select('id, order_id, reviewed')
    .eq('order_id', orderId)

  if (error || !data) {
    return { editable: true }
  }

  const hasReviewedInvoice = data && data.some((i: any) => i.reviewed === true)
  return { editable: !hasReviewedInvoice }
}

export async function checkStorageInvoice (invoice: string) {
  const { data } = await supabaseClient.storage.from('invoices').list(undefined, { limit: 1, search: `${invoice}` })
  return { data }
}

export async function getSpecialMarketplaceIds () {
  try {
    const { data } = await supabaseClient.from('variables').select('*').eq('name', SPECIAL_MARKETPLACES).single()
    return data.value
  } catch (error) {
    console.log(error)
  }
}

export async function getSubscriptionById (id: number) {
  const { data } = await supabaseClient
    .from('subscriptions')
    .select('*')
    .eq('id', id)
    .limit(1)
    .single()

  if (data) return data
  else return null
}
